// Talks to GET /api/v1/admin/conversations/{id}/telemetry — the end-to-end transaction view for one
// conversation: every operation (turn) it produced, as a span tree with durations, dependencies, logs
// and exceptions attached to the span they occurred under. Mirrors what the Application Insights
// "end-to-end transaction" blade shows, served in-product so an operator needs neither portal access
// nor KQL. Auth uses the same bearer + identity hints as the other admin clients.
import { authHeaders, type AuthArgs } from './authArgs';

/** One telemetry item and everything that hung off it. */
export interface TelemetryNode {
  spanId?: string | null;
  parentId?: string | null;
  /** Conversation the item is tagged with, or attributed to when untagged. */
  conversationId?: string | null;
  name?: string | null;
  /** Request | Tool | Model | Redis | Messaging | HTTP | SQL | Dependency | Trace | Exception | CustomEvent */
  category?: string | null;
  dependencyType?: string | null;
  target?: string | null;
  /** Dependency data / URL / statement, exception message, or log body. */
  detail?: string | null;
  timestamp?: string | null;
  durationMs?: number | null;
  success?: boolean | null;
  resultCode?: string | null;
  /** 1 Verbose, 2 Information, 3 Warning, 4 Error — log rows only. */
  severityLevel?: number | null;
  /** Custom dimensions (ActionName, ConvId, request path, …) — the diagnosable bits. */
  properties?: Record<string, string> | null;
  /** Exception call stack, already formatted into lines. */
  callStack?: string | null;
  method?: string | null;
  assembly?: string | null;
  innermostType?: string | null;
  innermostMessage?: string | null;
  innermostMethod?: string | null;
  children: TelemetryNode[];
  logs: TelemetryNode[];
  exceptions: TelemetryNode[];
}

/**
 * One distributed trace and its span tree.
 *
 * `roots` is a list, not a single root: a conversation propagates one traceparent, so chat/init,
 * every chat/stream, the agent invocations and each TranscriptProjection job share an operationId
 * while each starts its own root span. A real PPE conversation had 16 roots in one operation.
 */
export interface TelemetryOperation {
  /** The trace id. Several chats can share one, so it does not identify a conversation on its own. */
  operationId: string;
  /** Conversation this trace was attributed to. */
  conversationId?: string | null;
  name?: string | null;
  startTime?: string | null;
  durationMs?: number | null;
  success?: boolean | null;
  roots: TelemetryNode[];
}

export interface ConversationTelemetry {
  conversationId: string;
  generatedAt: string;
  /** False when telemetry was still landing — App Insights ingestion lags by minutes. */
  complete: boolean;
  expectedOperationCount: number;
  presentOperationCount: number;
  operations: TelemetryOperation[];
  exceptions: TelemetryNode[];
  customEvents: TelemetryNode[];
}

export interface ConversationDiagnosticsResponse {
  /** 'live' — queried from Log Analytics just now. 'archive' — the pre-generated telemetry file. */
  source: 'live' | 'archive';
  telemetry: ConversationTelemetry;
}

/** Thrown for a clean "no telemetry" answer so the page can show guidance rather than an error. */
export class TelemetryNotFoundError extends Error {}

export async function getConversationTelemetry(
  conversationId: string,
  args: AuthArgs,
): Promise<ConversationDiagnosticsResponse> {
  const res = await fetch(
    `/api/v1/admin/conversations/${encodeURIComponent(conversationId)}/telemetry`,
    { method: 'GET', headers: authHeaders(args) },
  );

  if (res.status === 404) {
    const body = await res.json().catch(() => null);
    throw new TelemetryNotFoundError(
      body?.message ?? 'No telemetry found for this conversation.',
    );
  }
  if (!res.ok) {
    throw new Error(`telemetry ${res.status}: ${await res.text()}`);
  }

  return (await res.json()) as ConversationDiagnosticsResponse;
}

/** Longest range the server accepts for a log export; mirrors ConversationTelemetry:ExportMaxRange. */
export const LOG_EXPORT_MAX_HOURS = 24;

/**
 * Downloads every Log Analytics row between `start` and `end` as a zip (one JSON-lines file per table
 * plus a manifest). Open to any signed-in user; needs the diagnostics.log_export.enabled flag.
 */
export async function downloadLogExport(
  start: Date,
  end: Date,
  args: AuthArgs,
): Promise<{ filename: string; blob: Blob }> {
  const qs = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
  const res = await fetch(`/api/v1/admin/logs/export?${qs.toString()}`, {
    method: 'GET',
    headers: authHeaders(args),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message ?? `log export ${res.status}`);
  }

  const match = /filename="?([^";]+)"?/i.exec(res.headers.get('content-disposition') ?? '');
  return { filename: match?.[1] ?? 'cmsp-logs.zip', blob: await res.blob() };
}

/** Total span count under a node (itself included), for the operation summary line. */
export function countSpans(node: TelemetryNode | null | undefined): number {
  if (!node) return 0;
  return 1 + node.children.reduce((sum, c) => sum + countSpans(c), 0);
}

/** Every exception at or below a node — drives the error badge on a collapsed operation. */
export function collectExceptions(node: TelemetryNode | null | undefined): TelemetryNode[] {
  if (!node) return [];
  return [
    ...node.exceptions,
    ...node.children.flatMap((c) => collectExceptions(c)),
  ];
}

/** One row of the Logs & traces list: a trace or an exception, with the span it occurred under. */
export interface LogEntry {
  log: TelemetryNode;
  spanName: string | null;
  isException: boolean;
}

/**
 * Every trace AND exception at or below a node, tagged with the span it occurred under.
 *
 * Exceptions belong here as well as in the tree. The tree answers "where in the call graph did this
 * happen"; this list answers "what happened, in order" — and a failure is the single most important
 * thing to find when scanning that. App Insights makes the same call: its panel is "Traces & events
 * occurrences", not traces alone.
 *
 * Logs are deliberately NOT rendered as tree rows: one PPE conversation carried 319 of them against
 * 88 spans, and a single span owned 132.
 */
export function collectLogs(
  node: TelemetryNode | null | undefined,
  spanName?: string | null,
): LogEntry[] {
  if (!node) return [];
  const here = node.name ?? spanName ?? null;
  return [
    ...node.logs.map((log) => ({ log, spanName: here, isException: false })),
    ...node.exceptions.map((log) => ({ log, spanName: here, isException: true })),
    ...node.children.flatMap((c) => collectLogs(c, here)),
  ];
}

/** Total log count at or below a node, for the per-span count badge. */
export function countLogs(node: TelemetryNode | null | undefined): number {
  if (!node) return 0;
  return node.logs.length + node.children.reduce((n, c) => n + countLogs(c), 0);
}

/** App Insights severityLevel: 0 Verbose, 1 Information, 2 Warning, 3 Error, 4 Critical. */
export function severityLabel(level: number | null | undefined): string {
  switch (level) {
    case 0: return 'Verbose';
    case 1: return 'Information';
    case 2: return 'Warning';
    case 3: return 'Error';
    case 4: return 'Critical';
    default: return 'Log';
  }
}
